import { AssetLoader } from './AssetLoader.js';
import { AudioManager } from '../audio/AudioManager.js';
import { BuildWindow } from './BuildWindow.js';
import { BuildingWindow } from './BuildingWindow.js';
import { Camera } from './Camera.js';
import { Economy } from './Economy.js';
import { Effects } from './Effects.js';
import { Grid } from './Grid.js';
import { DEFAULT_THEME, Hud } from './Hud.js';
import { HomeWindow } from './HomeWindow.js';
import { IsoProjection } from './IsoProjection.js';
import { LevelUpWindow } from './LevelUpWindow.js';
import { Modal } from './Modal.js';
import { QuestWindow } from './QuestWindow.js';
import { Renderer } from './Renderer.js';
import { ShopWindow } from './ShopWindow.js';
import { Progression } from './progression.js';
import { productionRate } from './happiness.js';
import { constructionCost, hasFreeSlot, objectById, sessionTimeLimitMs } from './schema.js';
import { beginConstruction, catchUp, collect, hydrateState, initState, tick, upgrade } from './production.js';
import { elapsedSince, load as loadSave, SAVE_VERSION, save, savesSuppressed } from './SaveState.js';
import { isMusicMuted, subscribeMusicMuted } from './audioSettings.js';
import type { SavedGame, SavedPlacement } from './SaveState.js';
import type {
    Cell,
    Footprint,
    GameConfig,
    InitialPlacement,
    InitialPlacementState,
    Placement,
    PlacementState,
    PendingBuild,
} from '../types.js';

const CLICK_SLOP = 5;

const MAX_TICK_MS = 1000;

const TICK_INTERVAL_MS = 250;

const SAVE_INTERVAL_MS = 10_000;

interface ScreenPos {
    x: number;
    y: number;
}

export class GameEngine {
    readonly ready: Promise<void>;

    private readonly config: GameConfig;
    private readonly canvas: HTMLCanvasElement;
    private readonly ctx: CanvasRenderingContext2D;
    private readonly grid: Grid;
    private readonly iso: IsoProjection;
    private readonly camera: Camera;
    private readonly renderer: Renderer;
    private readonly assetLoader: AssetLoader;
    private readonly economy: Economy;
    private readonly effects: Effects;
    private readonly progression: Progression;
    private readonly audio: AudioManager;

    private hud!: Hud;
    private buildWindow!: BuildWindow;
    private buildingWindow!: BuildingWindow;
    private levelUpWindow!: LevelUpWindow;
    private questWindow!: QuestWindow;
    private shopWindow!: ShopWindow;
    private homeWindow!: HomeWindow;
    private sessionModal: Modal | null = null;

    private readonly gameId: string;
    private readonly sessionLimitMs: number | null;
    private readonly savedGame: SavedGame | null;
    private readonly onOptions: (() => void) | null;

    private hoveredCell: Cell | null = null;
    private placements: Placement[] = [];
    private running = false;
    private started = false;

    private pendingBuild: PendingBuild | null = null;

    private panning = false;
    private dragLast: ScreenPos = { x: 0, y: 0 };
    private pressStart: ScreenPos | null = null;

    private resizeObserver: ResizeObserver | null = null;
    private raf: number | null = null;
    private tickTimer: ReturnType<typeof setInterval> | null = null;
    private lastTickAt = 0;
    private lastSaveAt = 0;
    private sessionRemainingMs: number | null = null;
    private sessionLastAt = 0;
    private sessionStarted = false;
    private sessionExpired = false;
    private sessionFinalized = false;
    private initialized = false;
    private restoring = false;
    private destroyed = false;
    private startButton: HTMLButtonElement | null = null;
    private unsubscribeMute: (() => void) | null = null;

    private readonly onWindowResize = (): void => this.resize();

    private readonly onDocumentKeydown = (e: KeyboardEvent): void => {
        if (this.sessionExpired) return;
        if (e.key === 'Escape') this.cancelPlacement();
    };

    private readonly onVisibilityChange = (): void => {
        if (document.hidden) {
            this.persist();
            this.stop();
            this.audio.pauseMusic();
        } else if (this.started && !this.sessionExpired) {
            this.audio.resumeMusic();
            this.start();
        }
    };

    private readonly onPageHide = (): void => this.persist();

    private readonly onPointerDownEvent = (e: PointerEvent): void => this.onPointerDown(e);
    private readonly onPointerMoveEvent = (e: PointerEvent): void => this.onPointerMove(e);
    private readonly onPointerUpEvent = (e: PointerEvent): void => this.onPointerUp(e);
    private readonly onContextMenu = (e: MouseEvent): void => {
        if (!this.pendingBuild) return;
        e.preventDefault();
        this.cancelPlacement();
    };
    private readonly onWheel = (e: WheelEvent): void => {
        if (this.sessionExpired) return;
        e.preventDefault();
        const rect = this.canvas.getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
        this.camera.setZoomAt(px, py, this.camera.zoom * factor);
        this.renderFrame();
    };
    private readonly onDocumentClick = (event: Event): void => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('button');
        if (!(button instanceof HTMLButtonElement)) return;

        this.audio.playEffect('click');
    };
    private readonly onStartButtonClick = (): void => {
        this.audio.playOpening();
        this.start();
    };

    constructor(config: GameConfig, gameId = '', onOptions: (() => void) | null = null) {
        this.config = config;
        this.gameId = gameId;
        this.onOptions = onOptions;
        this.sessionLimitMs = sessionTimeLimitMs(config);
        this.savedGame = gameId ? loadSave(gameId) : null;
        this.canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
        this.ctx = this.canvas.getContext('2d')!;

        const world = config.world ?? {};
        const cellSize = world.cellSize ?? 1;

        this.grid = new Grid({
            width: world.gridWidth ?? 16,
            height: world.gridHeight ?? 16,
            cellSize,
        });

        this.iso = new IsoProjection({
            tileWidth: world.tileWidth ?? 64,
            tileHeight: world.tileHeight ?? 32,
            cellSize,
        });

        this.camera = new Camera(this.canvas, this.ctx, this.iso, this.grid);
        this.effects = new Effects(this.camera);
        const assetsBase = gameId ? `assets/${gameId}/` : '';
        this.assetLoader = new AssetLoader(assetsBase);
        this.audio = new AudioManager(assetsBase, config.audio, gameId);
        this.renderer = new Renderer(
            this.ctx,
            this.grid,
            this.iso,
            this.assetLoader,
            this.camera,
            world.backgroundColor ?? '#1c1504',
            world.ground?.color ?? '#6a9a4a',
            world.ground?.sprite,
            world.background,
            world.foreground,
            world.foregroundOpacity ?? 0.6,
            this.effects,
            world.ground?.gridColor === undefined ? undefined : world.ground.gridColor,
        );

        this.economy = new Economy(config, (id, value) => {
            this.hud?.setResource(id, value);
            if (!this.restoring) this.checkProgression();
        });
        this.progression = new Progression(config);

        this.ready = this.init();
    }

    private async init(): Promise<void> {
        this.setupHeader();
        this.setupCanvas();
        this.setupEventListeners();
        this.setupButtonClickAudio();

        this.hud = new Hud({
            config: this.config,
            assetLoader: this.assetLoader,
            root: document.getElementById('hud') as HTMLElement,
        });

        await this.assetLoader.loadAll(this.config, this.hud.spriteUrls());
        if (this.destroyed) return;
        this.audio.register();

        this.buildPlacements();
        this.hud.build();
        this.hud.setViewport(this.camera.width, this.camera.height);
        this.setupWindows();

        this.restoreSavedGame();
        this.refreshPlanner();
        this.checkProgression();
        this.initialized = true;
        if (this.savedGame) this.persist();

        this.renderFrame();

        this.startButton = document.getElementById('startButton') as HTMLButtonElement;
        this.startButton.addEventListener('click', this.onStartButtonClick, { once: true });
    }

    private setupButtonClickAudio(): void {
        document.addEventListener('click', this.onDocumentClick);
    }

    private setupHeader(): void {
        const container = document.getElementById('gameContainer');
        if (container) {
            container.dataset.theme = this.config.ui?.interface || DEFAULT_THEME;
        }

        const { gameTitle, gameSubTitle, description, gameIcon } = this.config;
        if (gameTitle) {
            document.getElementById('gameTitle')!.textContent = gameTitle;
            document.title = gameTitle;
        }
        const subtitle = document.getElementById('gameSubTitle')!;
        subtitle.classList.toggle('hidden', !gameSubTitle);
        if (gameSubTitle) subtitle.textContent = gameSubTitle;
        if (description) {
            document.getElementById('gameDescription')!.textContent = description;
        }
        const icon = document.getElementById('gameLSIcon') as HTMLImageElement;
        if (gameIcon) icon.src = this.assetLoader.resolve(gameIcon);
        else icon.classList.add('hidden');
    }

    private setupWindows(): void {
        this.buildWindow = new BuildWindow({
            config: this.config,
            assetLoader: this.assetLoader,
            economy: this.economy,
            isUnlocked: (objectId) => this.progression.isUnlocked(objectId),
            hasSlot: (objectId) => hasFreeSlot(
                this.config,
                objectById(this.config, objectId),
                this.placements,
                this.progression.slotSnapshot(),
            ),
            categorySlots: (categoryId) => this.categorySlots(categoryId),
            onBuild: (objectId) => this.beginPlacement(objectId),
        });

        this.buildingWindow = new BuildingWindow({
            config: this.config,
            assetLoader: this.assetLoader,
            economy: this.economy,
            onCollect: (placement) => this.onCollect(placement),
            onUpgrade: (placement) => this.onUpgrade(placement),
            onDemolish: (placement) => this.onDemolish(placement),
            onMove: (placement) => this.beginMove(placement),
        });

        this.levelUpWindow = new LevelUpWindow({ assetLoader: this.assetLoader });

        this.questWindow = new QuestWindow({
            config: this.config,
            assetLoader: this.assetLoader,
            progression: this.progression,
            economy: this.economy,
            onClaim: () => {
                this.audio.playEffect('questAnswer');
                this.refreshPlanner();
            },
        });

        this.shopWindow = new ShopWindow({ config: this.config, economy: this.economy });

        this.homeWindow = new HomeWindow({
            config: this.config,
            progression: this.progression,
            economy: this.economy,
        });

        this.sessionModal = new Modal({
            title: 'Session ended',
            variant: 'session',
            dismissible: false,
        });
        this.sessionModal.body.classList.add('session-ended');
        this.sessionModal.body.textContent = 'Your session has ended.';

        this.hud.on('hammer', () => {
            if (!this.sessionExpired) this.buildWindow.toggle();
        });
        this.hud.on('planner', () => {
            if (this.sessionExpired) return;
            this.audio.playEffect('questOpen');
            this.questWindow.toggle(this.placements);
        });
        this.hud.on('book', () => {
            if (this.sessionExpired) return;
            this.audio.playEffect('questOpen');
            this.questWindow.toggle(this.placements);
        });
        this.hud.on('shop', () => {
            if (this.sessionExpired) return;
            this.audio.playEffect('click');
            this.shopWindow.toggle();
        });
        this.hud.on('home', () => {
            if (this.sessionExpired) return;
            this.audio.playEffect('click');
            this.homeWindow.toggle(this.placements);
        });
        this.hud.on('options', () => {
            if (this.sessionExpired) return;
            this.audio.playEffect('click');
            this.onOptions?.();
        });

        this.hud.setOptionsMuted(isMusicMuted());
        this.unsubscribeMute = subscribeMusicMuted((muted) => {
            this.hud?.setOptionsMuted(muted);
        });

    }

    private refreshPlanner(): void {
        this.hud?.setPlannerCount(this.progression.openCount(this.placements, this.economy));
    }

    private checkProgression(): void {
        if (!this.progression) return;

        const due = this.progression.collectDue(this.economy, this.placements);
        if (!due.length) return;

        due.forEach((milestone) => {
            const unlocked = this.progression.applyMilestoneRewards(milestone);
            this.hud?.setLevel(this.progression.level, milestone.popup?.title);
            this.audio.playEffect('victory');
            this.levelUpWindow?.open(milestone, unlocked);
        });

        this.refreshPlanner();
    }

    private beginPlacement(objectId: string): void {
        if (this.sessionExpired) return;
        const object = objectById(this.config, objectId);
        if (!object) return;
        if (!this.progression.isUnlocked(objectId)) return;

        if (!hasFreeSlot(this.config, object, this.placements, this.progression.slotSnapshot())) return;

        this.pendingBuild = { object, cost: constructionCost(object, 1) };
        this.canvas.classList.add('placing');
        this.renderFrame();
    }

    /**
     * Picks up an already-placed building for relocation, reusing the same
     * pendingBuild/ghost/cancel machinery as placing a new one — `moving`
     * just marks that dropAt() should relocate it instead of buying a new
     * one. The footprint is cleared immediately so the destination check
     * (and hovering back near the original cell) doesn't collide with the
     * building's own old spot; cancelPlacement() re-occupies it if the move
     * is abandoned.
     */
    private beginMove(placement: Placement): void {
        if (this.sessionExpired) return;
        this.buildingWindow?.close();
        this.grid.clear(placement.col, placement.row, placement.footprint);
        this.pendingBuild = { object: placement.object, cost: [], moving: placement };
        this.canvas.classList.add('placing');
        this.renderFrame();
    }

    private cancelPlacement(): void {
        if (!this.pendingBuild) return;
        const { moving } = this.pendingBuild;
        if (moving) this.grid.occupy(moving.col, moving.row, moving.footprint, moving.objectId);
        this.pendingBuild = null;
        this.canvas.classList.remove('placing');
        this.renderFrame();
    }

    private canDropAt(cell: Cell | null): boolean {
        if (this.sessionExpired) return false;
        if (!this.pendingBuild || !cell) return false;
        const { object, cost } = this.pendingBuild;
        return this.grid.isFree(cell.col, cell.row, object.footprint) && this.economy.canAfford(cost);
    }

    private dropAt(cell: Cell): boolean {
        if (this.sessionExpired) return false;
        if (!this.canDropAt(cell) || !this.pendingBuild) return false;

        const { object, moving } = this.pendingBuild;
        const footprint = object.footprint ?? { w: 1, h: 1 };

        if (moving) {
            this.grid.occupy(cell.col, cell.row, footprint, object.id);
            moving.col = cell.col;
            moving.row = cell.row;

            const centre = this.grid.footprintToWorld(cell.col, cell.row, footprint);
            this.effects.pop(centre.x, centre.z);

            this.sortPlacements();
            this.pendingBuild = null;
            this.canvas.classList.remove('placing');
            this.persist();
            this.renderFrame();
            return true;
        }

        const { cost } = this.pendingBuild;
        if (!this.economy.spend(cost)) return false;

        this.grid.occupy(cell.col, cell.row, footprint, object.id);

        const state = initState(object, {});
        beginConstruction(object, state);

        this.placements.push({
            object,
            objectId: object.id,
            col: cell.col,
            row: cell.row,
            footprint,
            state,
        });
        const centre = this.grid.footprintToWorld(cell.col, cell.row, footprint);
        this.effects.pop(centre.x, centre.z);

        this.sortPlacements();
        this.refreshPlanner();
        this.checkProgression();
        this.cancelPlacement();
        return true;
    }

    private onCollect(placement: Placement): void {
        if (this.sessionExpired) return;
        if (!collect(placement, this.economy)) return;

        const centre = this.grid.footprintToWorld(
            placement.col, placement.row, placement.footprint,
        );
        this.effects.burst(centre.x, centre.z, placement.object.color ?? '#ffc949');
        this.renderFrame();
    }

    private onUpgrade(placement: Placement): void {
        if (this.sessionExpired) return;
        if (!upgrade(placement, this.economy)) return;

        this.checkProgression();
        this.renderFrame();
    }

    private onDemolish(placement: Placement): void {
        if (this.sessionExpired) return;
        const index = this.placements.indexOf(placement);
        if (index === -1) return;

        collect(placement, this.economy);
        this.grid.clear(placement.col, placement.row, placement.footprint);
        this.placements.splice(index, 1);

        this.buildingWindow?.close();
        this.refreshPlanner();
        this.checkProgression();
        this.persist();
        this.renderFrame();
    }

    /** Slot usage for a capped category, or null when it is uncapped. */
    private categorySlots(categoryId: string): { used: number; limit: number } | null {
        const category = (this.config.categories ?? []).find((c) => c.id === categoryId);
        if (!category) return null;

        const limit = this.progression.slotSnapshot()[categoryId] ?? category.slots;
        if (limit == null) return null;

        const inCategory = (this.config.objects ?? [])
            .filter((o) => o.category === categoryId)
            .map((o) => o.id);
        const used = this.placements.filter(({ objectId }) => inCategory.includes(objectId)).length;

        return { used, limit };
    }

    private placementAt(col: number, row: number): Placement | null {
        return this.placements.find(({ col: c, row: r, footprint }) =>
            col >= c && col < c + footprint.w && row >= r && row < r + footprint.h,
        ) ?? null;
    }

    private setupCanvas(): void {
        this.resize();

        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.resize());
            this.resizeObserver.observe(this.canvas);
        }
    }

    private buildPlacements(): void {
        const saved = this.savedGame;
        // A save replaces the authored layout.
        const seed: (SavedPlacement | InitialPlacement)[] = saved
            ? saved.placements
            : (this.config.initialPlacements ?? []);

        seed.forEach(({ object: objectId, col, row, state }) => {
            const object = objectById(this.config, objectId);
            if (!object) {
                console.warn(`Unknown object in initialPlacements: ${objectId}`);
                return;
            }

            const footprint = object.footprint ?? { w: 1, h: 1 };
            if (!this.grid.occupy(col, row, footprint, objectId)) {
                console.warn(`Cannot place ${objectId} at ${col},${row} — out of bounds or taken`);
                return;
            }

            this.placements.push({
                object, objectId, col, row, footprint,
                state: saved
                    ? hydrateState(object, state as PlacementState)
                    : initState(object, (state as InitialPlacementState) ?? {}),
            });
        });

        if (!saved) this.scatterScenery();

        this.sortPlacements();
    }

    private scatterScenery(): void {
        const authored = new Set((this.config.initialPlacements ?? []).map(({ object }) => object));

        (this.config.objects ?? [])
            .filter((object) => object.onMap && !authored.has(object.id))
            .forEach((object) => {
                const footprint = object.footprint ?? { w: 1, h: 1 };
                if (footprint.w > this.grid.width || footprint.h > this.grid.height) return;

                const cell = this.randomFreeCell(footprint);
                if (!cell) return;

                this.grid.occupy(cell.col, cell.row, footprint, object.id);
                this.placements.push({
                    object,
                    objectId: object.id,
                    col: cell.col,
                    row: cell.row,
                    footprint,
                    state: initState(object, {}),
                });
            });
    }

    private randomFreeCell(footprint: Footprint): Cell | null {
        const cols = this.grid.width - footprint.w + 1;
        const rows = this.grid.height - footprint.h + 1;
        const budget = Math.min(cols * rows, 1000);

        for (let attempt = 0; attempt < budget; attempt++) {
            const col = Math.floor(Math.random() * cols);
            const row = Math.floor(Math.random() * rows);
            if (this.grid.isFree(col, row, footprint)) return { col, row };
        }
        return null;
    }

    private restoreSavedGame(): void {
        const saved = this.savedGame;
        if (!saved) return;

        this.restoring = true;
        try {
            this.progression.restore(saved.progression);
            this.economy.restore(saved.balances);

            this.progression.restoreSlots(saved.slots, saved.slots === undefined);

            const now = Date.now();
            const away = elapsedSince(saved.savedAt, now);
            const productionAway = this.restoreSession(saved, away, now);
            if (productionAway <= 0) return;

            this.catchUpProduction(productionAway);
        } finally {
            this.restoring = false;
        }
    }

    private restoreSession(saved: SavedGame, away: number, now: number): number {
        if (this.sessionLimitMs == null) return away;

        const savedRemaining = saved.session?.remainingMs;
        if (saved.session == null) {
            return away;
        }

        if (typeof savedRemaining !== 'number' || !Number.isFinite(savedRemaining)) {
            this.sessionStarted = true;
            this.sessionRemainingMs = 0;
            this.sessionLastAt = now;
            this.sessionExpired = true;
            this.hud.setSessionTime(0);
            return 0;
        }

        this.sessionStarted = true;
        this.sessionRemainingMs = Math.min(this.sessionLimitMs, Math.max(0, savedRemaining));
        const activeAway = Math.min(away, this.sessionRemainingMs);
        this.sessionRemainingMs -= activeAway;
        this.sessionLastAt = now;
        this.hud.setSessionTime(this.sessionRemainingMs);
        if (this.sessionRemainingMs <= 0) this.sessionExpired = true;
        return activeAway;
    }

    private persist(): void {
        if (savesSuppressed()) return;
        if (!this.gameId || !this.initialized || (!this.started && !this.savedGame)) return;

        const now = Date.now();
        const sessionElapsed = this.advanceSession(now);
        if (sessionElapsed != null) {
            if (sessionElapsed > 0) this.catchUpProduction(sessionElapsed);
            this.lastTickAt = performance.now();
        } else if (this.started) {
            const elapsed = Math.max(0, performance.now() - this.lastTickAt);
            if (elapsed > 0) this.catchUpProduction(elapsed);
            this.lastTickAt = performance.now();
        }
        if (this.sessionExpired && this.started) this.finishSession();

        const slots = this.progression.slotSnapshot();

        const game: SavedGame = {
            version: SAVE_VERSION,
            savedAt: now,
            balances: this.economy.snapshot(),
            progression: this.progression.snapshot(),
            slots,
            placements: this.placements.map(({ objectId, col, row, state }) => ({
                object: objectId, col, row, state,
            })),
        };

        if (this.sessionLimitMs != null && this.sessionStarted) {
            game.session = { remainingMs: this.sessionRemainingMs ?? 0 };
        }

        save(this.gameId, game);
    }

    private catchUpProduction(elapsedMs: number): boolean {
        if (elapsedMs <= 0) return false;
        // Real time drives construction; happiness only scales the output.
        const rate = productionRate(this.config);

        let changed = false;
        this.placements.forEach((placement) => {
            if (catchUp(placement, this.economy, elapsedMs, rate)) changed = true;
        });
        return changed;
    }

    private sortPlacements(): void {
        // Painter's algorithm: draw from back to front.
        this.placements.sort((a, b) => {
            const depthA = (a.col + a.footprint.w - 1) + (a.row + a.footprint.h - 1);
            const depthB = (b.col + b.footprint.w - 1) + (b.row + b.footprint.h - 1);
            return depthA - depthB || a.row - b.row;
        });
    }

    private resize(): void {
        if (this.camera.applySize()) {
            this.hud?.setViewport(this.camera.width, this.camera.height);
        }
        this.renderFrame();
    }

    private setupEventListeners(): void {
        window.addEventListener('resize', this.onWindowResize);

        this.canvas.addEventListener('pointerdown', this.onPointerDownEvent);
        this.canvas.addEventListener('pointermove', this.onPointerMoveEvent);
        this.canvas.addEventListener('pointerup', this.onPointerUpEvent);
        this.canvas.addEventListener('pointerleave', this.onPointerUpEvent);

        document.addEventListener('keydown', this.onDocumentKeydown);
        this.canvas.addEventListener('contextmenu', this.onContextMenu);
        this.canvas.addEventListener('wheel', this.onWheel, { passive: false });

        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('pagehide', this.onPageHide);
    }

    private onPointerDown(e: PointerEvent): void {
        if (this.sessionExpired) return;
        this.pressStart = { x: e.clientX, y: e.clientY };
        this.dragLast = { x: e.clientX, y: e.clientY };

        // What the drag does depends on what is under it: a press on a
        // building picks it up for a move (beginMove() clears its cell and
        // the ghost follows the cursor), while empty ground pans the board.
        // While a build is already pending the drag pans, so the player can
        // still reach the cell they want to drop on.
        if (!this.pendingBuild) {
            const cell = this.cellAtPointer(e);
            const placement = cell ? this.placementAt(cell.col, cell.row) : null;
            if (placement) this.beginMove(placement);
            else this.startPanning();
        } else {
            this.startPanning();
        }

        try {
            this.canvas.setPointerCapture(e.pointerId);
        } catch {
            /* Pointer capture is optional. */
        }
    }

    private startPanning(): void {
        this.panning = true;
        this.canvas.classList.add('panning');
    }

    private stopPanning(): void {
        this.panning = false;
        this.canvas.classList.remove('panning');
    }

    private onPointerUp(e: PointerEvent): void {
        if (!this.pressStart) return;
        this.stopPanning();

        if (e.pointerId !== undefined && this.canvas.hasPointerCapture(e.pointerId)) {
            this.canvas.releasePointerCapture(e.pointerId);
        }

        if (e.type !== 'pointerup') {
            // The pointer left the board mid-move: abandon it.
            this.pressStart = null;
            if (this.pendingBuild?.moving) this.cancelPlacement();
            return;
        }

        const travel = Math.hypot(e.clientX - this.pressStart.x, e.clientY - this.pressStart.y);
        this.pressStart = null;

        const moving = this.pendingBuild?.moving;
        if (moving) {
            if (travel <= CLICK_SLOP) {
                // Pressing a building without dragging just reopens its window.
                this.cancelPlacement();
                this.buildingWindow?.open(moving);
            } else if (this.canDropAt(this.hoveredCell)) {
                this.dropAt(this.hoveredCell!);
            } else {
                this.cancelPlacement();
            }
            return;
        }

        if (travel > CLICK_SLOP) return;

        this.onTap(e);
    }

    private onTap(e: PointerEvent): void {
        if (this.sessionExpired) return;
        const rect = this.canvas.getBoundingClientRect();
        const { x, z } = this.camera.canvasToWorld(e.clientX - rect.left, e.clientY - rect.top);
        const cell = this.grid.worldToCell(x, z);
        if (!cell) return;

        if (this.pendingBuild) {
            this.dropAt(cell);
            return;
        }

        const placement = this.placementAt(cell.col, cell.row);
        if (placement) this.buildingWindow?.open(placement);
    }

    private onPointerMove(e: PointerEvent): void {
        if (this.sessionExpired) return;

        if (this.panning) {
            const dx = e.clientX - this.dragLast.x;
            const dy = e.clientY - this.dragLast.y;
            this.dragLast = { x: e.clientX, y: e.clientY };
            this.camera.panByScreenDelta(dx, dy);
        }

        this.updateHover(e);
        this.renderFrame();
    }

    private updateHover(e: PointerEvent): void {
        this.setHoveredCell(this.cellAtPointer(e));
    }

    private cellAtPointer(e: PointerEvent): Cell | null {
        const rect = this.canvas.getBoundingClientRect();
        const { x, z } = this.camera.canvasToWorld(e.clientX - rect.left, e.clientY - rect.top);
        return this.grid.worldToCell(x, z);
    }

    private setHoveredCell(cell: Cell | null): void {
        this.hoveredCell = cell;
        const debug = document.getElementById('hudDebug');
        if (!debug) return;

        if (!cell) {
            debug.textContent = '–';
            return;
        }
        const occupant = this.grid.occupantAt(cell.col, cell.row);
        debug.textContent = `cell ${cell.col},${cell.row}` + (occupant ? ` — ${occupant}` : ' — free');
    }

    private start(): void {
        if (this.destroyed) return;
        const firstStart = !this.started;
        if (firstStart) {
            this.started = true;
            this.beginSession();
        }

        if (this.sessionStarted) {
            const sessionElapsed = this.advanceSession(Date.now());
            if (sessionElapsed != null && sessionElapsed > 0) {
                this.catchUpProduction(sessionElapsed);
                this.lastTickAt = performance.now();
            }
        }

        document.getElementById('loadingScreen')!.classList.add('hidden');
        document.getElementById('hud')!.classList.remove('hidden');

        if (this.sessionExpired) {
            this.finishSession();
            this.persist();
            return;
        }

        if (firstStart) {
            this.lastTickAt = performance.now();
            this.lastSaveAt = this.lastTickAt;
            this.persist();
        }

        this.startSimulation();

        if (this.running) return;
        this.running = true;

        const loop = () => {
            if (!this.running) return;
            this.renderFrame();
            this.raf = requestAnimationFrame(loop);
        };
        this.raf = requestAnimationFrame(loop);
    }

    private stop(): void {
        this.running = false;
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
    }

    // Keep simulation independent from requestAnimationFrame.
    private startSimulation(): void {
        if (this.tickTimer || this.sessionExpired) return;
        this.lastTickAt = performance.now();
        this.tickTimer = setInterval(() => this.tickProduction(), TICK_INTERVAL_MS);
    }

    private stopSimulation(): void {
        if (this.tickTimer) clearInterval(this.tickTimer);
        this.tickTimer = null;
    }

    private beginSession(): void {
        if (this.sessionLimitMs == null || this.sessionStarted || this.sessionExpired) return;

        this.sessionStarted = true;
        this.sessionRemainingMs = this.sessionLimitMs;
        this.sessionLastAt = Date.now();
        this.hud.setSessionTime(this.sessionRemainingMs);
    }

    private advanceSession(now: number): number | null {
        if (this.sessionLimitMs == null || !this.sessionStarted) return null;
        if (this.sessionExpired) return 0;

        const elapsed = elapsedSince(this.sessionLastAt, now);
        const remaining = this.sessionRemainingMs ?? 0;
        const active = Math.min(elapsed, remaining);
        this.sessionLastAt = now;
        this.sessionRemainingMs = remaining - active;
        this.hud?.setSessionTime(this.sessionRemainingMs);
        if (this.sessionRemainingMs <= 0) this.sessionExpired = true;
        return active;
    }

    private finishSession(): void {
        if (!this.sessionExpired || this.sessionFinalized) return;

        this.sessionFinalized = true;
        this.sessionRemainingMs = 0;
        this.pendingBuild = null;
        this.pressStart = null;
        this.stopPanning();
        this.canvas.classList.remove('placing');
        this.hud?.setSessionTime(0);
        this.hud?.setEnabled(false);
        this.stopSimulation();
        this.stop();
        this.renderFrame();
        if (this.started) this.sessionModal?.open();
    }

    destroy(): void {
        if (this.destroyed) return;
        if (this.initialized) this.persist();
        this.destroyed = true;
        this.unsubscribeMute?.();
        this.unsubscribeMute = null;
        this.stop();
        this.stopSimulation();
        this.audio.destroy();
        this.hud?.destroy();
        this.buildWindow?.close();
        this.buildingWindow?.close();
        this.questWindow?.close();
        this.shopWindow?.close();
        this.homeWindow?.close();
        this.levelUpWindow?.close();
        this.sessionModal?.close();
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.onWindowResize);
        this.canvas.removeEventListener('pointerdown', this.onPointerDownEvent);
        this.canvas.removeEventListener('pointermove', this.onPointerMoveEvent);
        this.canvas.removeEventListener('pointerup', this.onPointerUpEvent);
        this.canvas.removeEventListener('pointerleave', this.onPointerUpEvent);
        this.canvas.removeEventListener('contextmenu', this.onContextMenu);
        this.canvas.removeEventListener('wheel', this.onWheel);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('pagehide', this.onPageHide);
        this.startButton?.removeEventListener('click', this.onStartButtonClick);
    }

    private tickProduction(): void {
        const now = performance.now();
        const elapsed = Math.min(Math.max(0, now - this.lastTickAt), MAX_TICK_MS);
        this.lastTickAt = now;
        if (elapsed <= 0) return;

        const sessionElapsed = this.advanceSession(Date.now());
        const productionElapsed = sessionElapsed == null
            ? elapsed
            : Math.min(elapsed, sessionElapsed);

        // Real time drives construction; happiness only scales the output.
        const rate = productionRate(this.config);

        let changed = false;
        if (productionElapsed > 0) {
            this.placements.forEach((placement) => {
                if (tick(placement, this.economy, productionElapsed, rate)) changed = true;
            });
        }

        if (sessionElapsed != null) {
            const unprocessed = sessionElapsed - productionElapsed;
            if (unprocessed > 0 && this.catchUpProduction(unprocessed)) changed = true;
        }

        if (this.sessionExpired) {
            if (changed) this.buildingWindow?.refresh();
            this.finishSession();
            this.persist();
            return;
        }

        if (now - this.lastSaveAt >= SAVE_INTERVAL_MS) {
            this.lastSaveAt = now;
            this.persist();
            if (this.sessionExpired) return;
        }

        if (!changed) return;

        this.buildingWindow?.refresh();
        if (!this.running) this.renderFrame();
    }

    private renderFrame(): void {
        if (this.destroyed) return;
        if (this.camera.applySize()) {
            this.hud?.setViewport(this.camera.width, this.camera.height);
        }

        this.effects.prune();

        // While relocating, the building being moved is drawn as the ghost
        // at the hovered cell instead — hiding the real sprite at its old
        // spot avoids drawing it twice.
        const moving = this.pendingBuild?.moving;
        const visible = moving ? this.placements.filter((p) => p !== moving) : this.placements;

        this.renderer.render(
            visible,
            this.hoveredCell,
            this.pendingBuild,
            this.canDropAt(this.hoveredCell),
        );
    }
}
